import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppContext from './AppContext';

// Clé pour stocker les routes dans sessionStorage
const NAVIGATION_HISTORY_KEY = "kappela_navigation_history";
const MAX_HISTORY_LENGTH = 10; // Limiter la taille pour éviter de surcharger sessionStorage

// Liste des routes principales (bottom bar) qui n'ont pas de retour
const MAIN_ROUTES = [
  '/IndexEspace',
  '/',
  '/ProductsList',
  '/GroupeListe',
  '/Statistics',
  '/PaymentsSheetsIndex'
];

// Liste des sous-routes et leurs routes parentes
const SUB_ROUTES_MAP = {
  '/ProductCard/': '/ProductsList',
  '/GroupeDetail/': '/GroupeListe',
  '/ajouterProduit/': '/ProductsList'
};

// Créer le contexte
export const NavigationContext = createContext({
  // Naviguer vers une route, en sauvegardant l'historique
  navigateTo: () => {},
  // Naviguer vers la route précédente
  goBack: () => {},
  // Vérifier si la route actuelle est une route principale
  isMainRoute: () => false,
  // Histoire de navigation
  navigationHistory: [],
  // Route actuelle
  currentRoute: '',
  // Route précédente
  previousRoute: '',
});

// Hook personnalisé
export const useNavigation = () => useContext(NavigationContext);

// Provider du contexte de navigation
export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setupTelegramBackButton } = useContext(AppContext);
  
  // Récupérer l'historique de navigation depuis sessionStorage
  const getNavigationHistory = useCallback(() => {
    try {
      const history = sessionStorage.getItem(NAVIGATION_HISTORY_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique:", error);
      return [];
    }
  }, []);
  
  // Sauvegarder l'historique de navigation dans sessionStorage
  const saveNavigationHistory = useCallback((history) => {
    try {
      // Limiter la taille de l'historique
      const limitedHistory = history.slice(-MAX_HISTORY_LENGTH);
      sessionStorage.setItem(NAVIGATION_HISTORY_KEY, JSON.stringify(limitedHistory));
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'historique:", error);
    }
  }, []);
  
  // Vérifier si une route est une route principale (qui n'a pas besoin de bouton retour)
  const isMainRoute = useCallback((route) => {
    // Vérifier si c'est une route principale
    if (MAIN_ROUTES.includes(route)) {
      return true;
    }
    
    // Vérifier si c'est une sous-route connue
    for (const prefix in SUB_ROUTES_MAP) {
      if (route.startsWith(prefix)) {
        return false; // Les sous-routes NE SONT PAS des routes principales
      }
    }
    
    return false;
  }, []);
  
  // Naviguer vers une route, en sauvegardant l'historique
  const navigateTo = useCallback((to) => {
    const history = getNavigationHistory();
    const currentPath = location.pathname;
    
    // Ne pas ajouter à l'historique si on va à la même page
    if (to === currentPath) return;
    
    // Ajouter la route actuelle à l'historique seulement si ce n'est pas une route principale
    if (!isMainRoute(currentPath)) {
      history.push(currentPath);
      saveNavigationHistory(history);
    }
    
    // Naviguer vers la nouvelle route
    navigate(to);
  }, [navigate, location.pathname, getNavigationHistory, saveNavigationHistory, isMainRoute]);
  
  // Naviguer vers la route précédente
  const goBack = useCallback(() => {
    const history = getNavigationHistory();
    const currentPath = location.pathname;
    
    // Vérifier si c'est une sous-route connue (comme GroupDetail ou ProductCard)
    for (const prefix in SUB_ROUTES_MAP) {
      if (currentPath.startsWith(prefix)) {
        // Si c'est une sous-route, naviguer vers la route parente
        navigate(SUB_ROUTES_MAP[prefix]);
        return;
      }
    }
    
    // Si on est sur une route principale, retourner à l'accueil
    if (isMainRoute(currentPath)) {
      navigate('/IndexEspace');
      return;
    }
    
    // S'il y a un historique, prendre la dernière route
    if (history.length > 0) {
      const previousRoute = history.pop();
      saveNavigationHistory(history);
      navigate(previousRoute);
    } else {
      // Sinon, retourner à l'accueil
      navigate('/IndexEspace');
    }
  }, [navigate, getNavigationHistory, saveNavigationHistory, location.pathname, isMainRoute]);
  
  // Mettre à jour l'historique et configurer le bouton retour de Telegram
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Configurer le bouton retour de Telegram si disponible
    if (setupTelegramBackButton) {
      // Les routes principales n'ont pas de bouton retour
      const showBackButton = !isMainRoute(currentPath);
      
      // Les sous-routes ont toujours un bouton retour
      const isSubRoute = Object.keys(SUB_ROUTES_MAP).some(prefix => 
        currentPath.startsWith(prefix)
      );
      
      // Afficher le bouton retour si c'est une sous-route ou si ce n'est pas une route principale
      if (isSubRoute || showBackButton) {
        setupTelegramBackButton(true, goBack);
      } else {
        setupTelegramBackButton(false);
      }
    }
  }, [location.pathname, isMainRoute, goBack, setupTelegramBackButton]);
  
  // Calculer la route précédente
  const previousRoute = React.useMemo(() => {
    const history = getNavigationHistory();
    return history.length > 0 ? history[history.length - 1] : '/IndexEspace';
  }, [getNavigationHistory]);
  
  // Valeur du contexte
  const contextValue = {
    navigateTo,
    goBack,
    isMainRoute,
    navigationHistory: getNavigationHistory(),
    currentRoute: location.pathname,
    previousRoute,
  };
  
  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationContext;